import  { Injectable } from '@angular/core';

import moment from 'moment-timezone';
import isEqual from 'lodash/isEqual';

import { Schedule, ScheduleHours, Exception } from '@shared/schedule/models';
import { getExceptions, getIntervals, getStartAndEndOfDay, getWorkingHours, inIntervals, timeToTimestamp } from '@shared/schedule/functions';
import { Intervals } from '@shared/schedule/types';
import { BlockedTime } from '@shared/blocked-time/models';
import { isTimeBlocked } from '@shared/blocked-time/functions';
import { ScheduleService } from './schedule.service';

export type Day = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';

const DAYS_IN_WEEK: Day[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DAYS_TO_CHECK = 730;

interface Capacity {
  min: number;
  max: number;
}

interface Resource {
  _id: string;
  capacity: Capacity;
}

interface Party {
  resources: string[];
  quoted_time: number;
  duration: number;
  size: number;
  waitlistId: string;
}

interface ReservationTime {
  begin: number;
  end: number;
  size: number;
}

interface HistogramValue {
  time: number;
  reserved: number;
}

@Injectable({ providedIn: 'root' })
export class ProviderScheduleService {
  timeFormat = 'h:mm A';
  private readonly weekDays = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };

  constructor(private readonly scheduleService: ScheduleService) { }

  /**
   * Check if the restaurant is open and set hours string
   *
   * @param scheduleHours
   * @param exceptions
   * @param timezone
   */
  isOpenNow(scheduleHours: ScheduleHours, exceptions: Exception[], timezone: string): {opened: boolean; message?: string, hours?: string} {
    if (isEqual({}, scheduleHours)) {
      return { opened: true };
    }

    const now = Date.now();
    const hours = this.getWorkingHours(scheduleHours, exceptions, now, timezone, true);
    if (inIntervals(hours, now, now)) {
      return { opened: true };
    }

    if (hours.length === 0) {
      return {
        opened: false,
        message: 'Sorry, we\'re closed today.',
      };
    }

    const startOfDay = moment.tz(timezone).startOf('day').valueOf();
    const workingHours: string[] = [];

    for (const [start, end] of hours) {
      if (end > startOfDay) {
        workingHours.push(`${moment.tz(start, timezone).format(this.timeFormat)} - ${moment.tz(end, timezone).format(this.timeFormat)}`);
      }
    }

    return {
      opened: false,
      hours: workingHours.join(' and '),
    };
  }

  isOpenedInDate(schedule: Schedule, date: number, timezone: string, isPublic = false) {
    const { hours, exceptions } = schedule;
    return this.getWorkingHours(
      hours,
      exceptions,
      date,
      timezone,
      isPublic,
    ).length > 0;
  }

  setTimeToDate(timestamp: number, date: number, timezone: string, nextDay = false): number {
    return timeToTimestamp(
      timestamp,
      moment.tz(date, timezone).format('D-M-YYYY'),
      timezone,
      nextDay,
    );
  }

  getAvailableTimeForReservation(data: any) {
    const {
      workTime,
      parties,
      bookingInterval,
      servicesDuration,
      resources,
      allowMultipleParties,
      partySizeForTimeSlots,
      publicReservationSize,
      partyId,
      bookingMinLimiter,
      blockedTimes = [],
      timezone,
      serviceIds = [],
      resourceIds = [],
    } = data;

    if (workTime.length === 0) {
      // Closed
      return [];
    }

    const reservedTime = this.getReservedTime(
      parties,
      resources,
      allowMultipleParties,
      partySizeForTimeSlots,
      publicReservationSize,
      partyId,
    );
    const availableTime: number[][] = [];
    for (const interval of workTime) {
      availableTime.push(...this.getAvailableTime(
        interval,
        reservedTime,
        bookingInterval,
        servicesDuration,
        bookingMinLimiter,
        blockedTimes,
        timezone,
        serviceIds,
        resourceIds,
      ));
    }

    return availableTime;
  }

  getReservedTime(
    parties: Party[],
    resource: Resource,
    allowMultipleParties: boolean,
    partySizeForTimeSlots: boolean,
    publicReservationSize: number,
    partyId?: string,
  ) {
    if (!resource) {
      return [];
    }
    const resourcesId = resource?._id ?? null;
    const reservations = parties.reduce<ReservationTime[]>(
      (acc, party) => {
        if (partyId === party.waitlistId) {
          return acc;
        }
        const partyResources = party?.resources || [];
        if (partyResources.includes(resourcesId)) {
          acc.push({
            begin: party.quoted_time,
            end: party.quoted_time + party.duration,
            size: partySizeForTimeSlots ? party.size : 1,
          });
        }

        return acc;
      },
      [],
    );

    const largerThanOrEqual = (num: number) => (element: number) => element >= num;

    // Create histogram list based on when and how many times parties were reserved
    // Push start and end time of party for first time or modify current one if available already
    // For start time of histogram reserved is 1 and increase only if a new party starts from start time or start time in duration of a new party
    // For end time of histogram reserved is 0 and increase only if a new party starts from end time or end time in duration of a new party
    const histogram: HistogramValue[] = [];
    reservations.forEach((reservation) => {
      const index = histogram
        .map(histogramItem => histogramItem.time)
        .findIndex(largerThanOrEqual(reservation.begin));
      if (index == -1) {
        histogram.push({
          time: reservation.begin,
          reserved: reservation.size,
        });
        histogram.push({
          time: reservation.end,
          reserved: 0,
        });
      } else {
        if (histogram[index].time == reservation.begin) {
          histogram[index].reserved += reservation.size;
        } else {
          histogram.splice(index, 0, {
            time: reservation.begin,
            reserved: reservation.size + (index > 0 ? histogram[index - 1].reserved : 0),
          });
        }
        let endIndex = histogram
          .map(histogramItem => histogramItem.time)
          .findIndex(largerThanOrEqual(reservation.end));
        if (endIndex == -1) {
          endIndex = histogram.length;
        }
        for (let i = index + 1; i < endIndex; i++) {
          histogram[i].reserved += reservation.size;
        }
        if (endIndex == histogram.length || histogram[endIndex].time > reservation.end) {
          histogram.splice(endIndex, 0, {
            time: reservation.end,
            reserved: histogram[endIndex - 1].reserved - reservation.size,
          });
        }
      }
    });

    // Get max reserved value depends on settings: <Allow Multiple Parties to Book a Resource> and <Count each person in a group as 1 spot each>
    // First case: both of buttons is unchecked: quantity of book parties only one time for special time
    // Second case: first button is checked and second button is unchecked: quantity of book parties can't be more than capacity of current resource
    // Third case: both of buttons is checked: size of book parties can't be more than capacity of current resource
    const requiredSize = partySizeForTimeSlots ? publicReservationSize : 1;
    const maxReservedForAvail = allowMultipleParties ? resource.capacity.max - requiredSize : 0;
    let available = true;

    // Return reserved time
    return histogram
      .reduce<number[][]>(
      (acc, histogramItem) => {
        if (available) {
          if (histogramItem.reserved > maxReservedForAvail) {
            available = false;
            acc.push([histogramItem.time, -1]);
          }
        } else if (histogramItem.reserved <= maxReservedForAvail) {
          available = true;
          acc[acc.length - 1][1] = histogramItem.time;
        }

        return acc;
      },
      [],
    );
  }

  getAvailableTime(
    workTime: number[],
    reservedTime: any,
    bookingInterval: any,
    servicesDuration: any,
    bookingMinLimiter: number,
    blockedTimes: BlockedTime[] = [],
    timezone?: string,
    serviceIds: string[] = [],
    resourceIds: string[] = [],
  ) {
    let endTime = 0;
    const availableTimes = [];
    const now = Date.now();
    const minStartTime = moment(now).add(bookingMinLimiter, 'minute').valueOf();

    while (endTime <= workTime[1] - servicesDuration) {
      const startTime = endTime || workTime[0];
      const endPartyTime = startTime + servicesDuration;
      endTime = startTime + bookingInterval;

      if (startTime < minStartTime || endPartyTime > workTime[1]) {
        continue;
      }

      if (blockedTimes.length && timezone && isTimeBlocked(startTime, timezone, blockedTimes, serviceIds, resourceIds).blocked) {
        continue;
      }

      const filteredTime = reservedTime.filter(time =>
        this.removeMilliseconds(time[0]) < this.removeMilliseconds(endPartyTime) &&
        this.removeMilliseconds(time[1]) > this.removeMilliseconds(startTime)
      );

      if (!filteredTime.length || !reservedTime.length) {
        availableTimes.push([startTime, endTime]);
      }
    }

    return availableTimes;
  }

  removeMilliseconds(timestamp: number) {
    const removeMilliseconds = 1000;

    return Math.floor(timestamp / removeMilliseconds);
  }

  // The same as date.service -> convertHoursToTimestamp
  convertHoursToTimestamp(timeString: string, timezone: string, day = 1, hours?: string[]): number {
    let time = moment.tz(timeString, this.timeFormat, timezone);
    /* eslint-disable no-magic-numbers */
    time = this.setTimeToComparisonFormat(time, day);
    let timeMs = time.valueOf();
    if (hours && timeMs < Number(hours[0])) {
      time.add(1, 'days');
      timeMs = time.valueOf();
    }

    /* eslint-enable no-magic-numbers */
    return timeMs;
  }

  setTimeToComparisonFormat(time, day = 1) {
    return time.set({ date: day });
  }

  isTimeInSchedule(schedule: Schedule, date: number, time: number, timezone: string, isPublic = false): boolean {
    const { hours, exceptions } = schedule;
    const intervals = this.getWorkingHours(hours, exceptions, date, timezone, isPublic);
    const selectedTimestamp = timeToTimestamp(
      time,
      moment.tz(date, timezone).format('D-M-YYYY'),
      timezone,
    );

    return inIntervals(intervals, selectedTimestamp, selectedTimestamp);
  }

  comparisonTime(time: number, scheduleTime: Intervals, servicesDuration = 0) {
    return inIntervals(scheduleTime, time, time + servicesDuration);
  }

  private setDaysInCorrectOrder(hours: ScheduleHours): ScheduleHours {
    const { Mon, Tue, Wed, Thu, Fri, Sat, Sun } = hours;

    return { Mon, Tue, Wed, Thu, Fri, Sat, Sun };
  }

  /**
   * Returns working hours for a nearest opened day
   * @param scheduleHours
   * @param exceptions
   * @param date
   * @param timezone
   */
  getNextWorkingHours(
    scheduleHours: ScheduleHours,
    exceptions: Exception[],
    date: number,
    timezone: string,
    isPublic = false,
  ) {
    let dayToCheckMoment = moment.tz(date, timezone).startOf('day');
    const now = moment.tz(timezone);

    // First check remaining hours for today
    const todayHours = this.getWorkingHours(scheduleHours, exceptions, date, timezone, isPublic);
    const futureHoursToday = todayHours.filter(([start]) => start > now.valueOf());

    if (futureHoursToday.length > 0) {
      return { workingHours: futureHoursToday, selectedDate: date };
    }

    // If no remaining hours today, check future days
    // Check up to next 2 years
    for (let i = 0; i < DAYS_TO_CHECK; i++) {
      dayToCheckMoment = dayToCheckMoment.clone().add(1, 'day');
      const dayOfWeek = dayToCheckMoment.format('ddd');
      const dateExceptions = getExceptions(exceptions, dayToCheckMoment.format('D-M-YYYY'), timezone);
      const dateClosedException = dateExceptions.find(exception => exception.closed || exception.publicDisabled);

      // Check exceptions first because they trump regular schedule
      if (dateExceptions.length) {
        if (!!dateClosedException) {
          // There's an exception and it's closed so keep iterating to find an open day
          continue;
        } else {
          // There's an exception and it's open, proceed
          break;
        }
      }

      // No exception for today
      if (scheduleHours[dayOfWeek].closed || scheduleHours[dayOfWeek].publicDisabled) {
        // Regular schedule closed, keep checking
        continue;
      } else {
        // Regular schedule open, proceed
        break;
      }
    }

    const nextDay = dayToCheckMoment.valueOf();

    const workingHours = this.getWorkingHours(
      scheduleHours,
      exceptions,
      nextDay,
      timezone,
      isPublic,
    );

    return { workingHours, selectedDate: nextDay };
  }

  /**
   * Returns working hours for a date including next day
   * @param scheduleHours
   * @param exceptions
   * @param date
   * @param timezone
   * @param isPublic
   */
  getWorkingHours(
    scheduleHours: ScheduleHours,
    exceptions: Exception[],
    date: number,
    timezone: string,
    isPublic = false,
  ): Intervals {
    return getWorkingHours(scheduleHours, exceptions, date, timezone, isPublic);
  }

  getWorkingHoursForDayGrid(schedule: Schedule, date: number, timezone: string): Array<[string, string]> {
    const { hours: scheduleHours, exceptions } = schedule;
    const intervals = getIntervals(scheduleHours, exceptions, date, timezone, false);
    const [startOfDay, startOfNextDay] = getStartAndEndOfDay(moment.tz(date, timezone));

    const endOfDay = moment.tz(startOfNextDay, timezone)
      .subtract(1, 'days')
      .endOf('day')
      .valueOf();

    const hours: Array<[string, string]> = [];
    for (const interval of intervals) {
      if (interval[1] < startOfDay || interval[0] > endOfDay) {
        continue;
      }
      const [start, end] = interval;
      hours.push([
        moment.tz(Math.max(start, startOfDay), timezone).format('HH:mm'),
        moment.tz(Math.min(end, endOfDay), timezone).format('HH:mm'),
      ]);
    }

    return hours;
  }

  getAroundTheClockSchedule(timezone: string): { hours: ScheduleHours } {
    const startOfDay = this.convertHoursToTimestamp('12:00 AM', timezone);
    const endOfDay = this.convertHoursToTimestamp('11:59 PM', timezone);

    return {
      hours: DAYS_IN_WEEK.reduce<ScheduleHours>((obj, key) => Object.assign(obj, {
        [key]: {
          hours: [[startOfDay, endOfDay]],
          closed: false,
          nextDay: false,
        },
      }), {} as ScheduleHours),
    };
  }
}
